import { AccessTime, CalendarToday } from '@mui/icons-material'
import { IconButton, InputAdornment } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import moment from 'moment'
import { useRef, useState } from 'react'
import { useController } from 'react-hook-form'
import { AppTextField } from './AppTextField'
import { useFormFieldError } from './FormCore'

export interface GenericDateTimePickerSplit<T = any> {
  value?: T
  label?: string
  onChange: (value: T) => void
  name: string
  disable?: boolean
  fullWidth?: boolean
  error?: boolean
  helperText?: string
}

export interface DateTimePickerSplitProps extends GenericDateTimePickerSplit {
  isWithoutAppForm?: boolean
}

export const DateTimePickerSplitComponent: React.FC<DateTimePickerSplitProps> = ({
  isWithoutAppForm,
  ...props
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {isWithoutAppForm ? (
        <GenericDateTimePickerSplit {...props} />
      ) : (
        <DateTimePickerSplitWithAppForm {...props} />
      )}
    </LocalizationProvider>
  )
}

const GenericDateTimePickerSplit: React.FC<GenericDateTimePickerSplit> = ({
  label,
  value,
  onChange,
  error,
  name,
  disable,
  fullWidth,
  helperText,
}) => {
  const [openDatePicker, setOpenDatePicker] = useState(false)
  const [openTimePicker, setOpenTimePicker] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const dateValue = value ? moment(value).toDate() : null
  const formattedValue = value ? moment(value).format('YYYY-MM-DD - HH:mm') : ''

  const handleDateChange = (newDate) => {
    if (!newDate) {
      onChange(null)
      return
    }

    const currentTime = value ? moment(value) : moment()
    const updatedDateTime = moment(newDate)
      .hours(currentTime.hours())
      .minutes(currentTime.minutes())
      .seconds(0)

    onChange(updatedDateTime.toDate())
  }

  const handleTimeChange = (newTime) => {
    if (!newTime) return

    const currentDate = value ? moment(value) : moment()
    const updatedDateTime = moment(currentDate)
      .hours(moment(newTime).hours())
      .minutes(moment(newTime).minutes())
      .seconds(0)

    onChange(updatedDateTime.toDate())
  }

  return (
    <div ref={anchorRef}>
      <DatePicker
        open={openDatePicker}
        onClose={() => setOpenDatePicker(false)}
        value={dateValue}
        onChange={handleDateChange}
        disabled={disable}
        PopperProps={{
          anchorEl: anchorRef.current,
        }}
        renderInput={() => <></>}
      />

      <TimePicker
        open={openTimePicker}
        onClose={() => setOpenTimePicker(false)}
        value={dateValue}
        onChange={handleTimeChange}
        disabled={disable}

        PopperProps={{
          anchorEl: anchorRef.current,
        }}
        views={['hours', 'minutes']}
        renderInput={() => <></>}
      />

      <AppTextField
        label={label}
        value={formattedValue}
        name={name}
        fullWidth={fullWidth}
        error={error}
        helperText={helperText}
        disabled={disable}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => !disable && setOpenDatePicker(true)}
                edge="end"
                size="small"
                disabled={disable}
              >
                <CalendarToday fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => !disable && setOpenTimePicker(true)}
                edge="end"
                size="small"
                disabled={disable}
              >
                <AccessTime fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  )
}

const DateTimePickerSplitWithAppForm: React.FC<GenericDateTimePickerSplit> = ({
  label,
  value,
  onChange,
  error,
  name,
  disable,
  fullWidth,
  helperText,
}) => {
  const { hasError, message } = useFormFieldError(name)
  const { field } = useController({ name })

  return (
    <GenericDateTimePickerSplit
      label={label}
      value={value ?? field.value}
      onChange={date => {
        onChange ? onChange(date) : field.onChange(date)
      }}
      error={error ?? hasError}
      name={name}
      disable={disable}
      fullWidth={fullWidth}
      helperText={helperText ?? message}
    />
  )
}